import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DateInput,
  dateFormatterDayMonthYearLong2Digits,
  HourMinuteInput,
  Spacer,
  TextCapitalized,
} from "@qivia/ui";
import { ButtonDS } from "@qivia/ui/src/designSystem/components/Button";
import styled from "styled-components";
import { Page } from "../../components/Page";
import { useAppSelector } from "../../../redux/hooks";
import { selectActiveDriverByPhone } from "../../qrCode/menuSlice";
import { selectLastSession } from "../../qrCode/driverKnownSlice";
import "../../../i18n/i18n";

const formatDateTime = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${dateFormatterDayMonthYearLong2Digits(date)} à ${pad(
    date.getHours(),
  )}h${pad(date.getMinutes())}`;
};

interface StartSessionStepProps {
  onSubmit: (date: Date) => void;
}

export const StartSessionStep = ({ onSubmit }: StartSessionStepProps) => {
  const { t } = useTranslation();
  const activeDriverByPhone = useAppSelector(selectActiveDriverByPhone);
  const lastSession = useAppSelector(selectLastSession);

  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [displayError, setDisplayError] = useState(false);

  const name = useMemo(
    () => activeDriverByPhone.driver?.firstName ?? "",
    [activeDriverByPhone],
  );

  const minDateTime = useMemo(() => {
    if (!lastSession) return null;
    return new Date(lastSession.endOfUsage ?? lastSession.startOfUsage);
  }, [lastSession]);

  const maxDateTime = useMemo(() => new Date(), []);

  const isBeforeEndOfUsage = useMemo(
    () => !!minDateTime && selectedDate < minDateTime,
    [minDateTime, selectedDate],
  );
  const isInFuture = useMemo(
    () => selectedDate > maxDateTime,
    [maxDateTime, selectedDate],
  );
  const hasError = useMemo(
    () => isBeforeEndOfUsage || isInFuture,
    [isBeforeEndOfUsage, isInFuture],
  );

  const dateError = useMemo(() => {
    if (isBeforeEndOfUsage && minDateTime) {
      return t("startSession.error.beforeEndOfUsage", {
        date: formatDateTime(minDateTime),
      });
    }
    if (isInFuture) {
      return t("startSession.error.inFuture", {
        date: formatDateTime(maxDateTime),
      });
    }
    return undefined;
  }, [isBeforeEndOfUsage, isInFuture, minDateTime, maxDateTime, t]);

  const handleSubmit = useCallback(() => {
    setDisplayError(hasError);
    if (hasError) return;
    onSubmit(selectedDate);
  }, [hasError, onSubmit, selectedDate]);

  return (
    <Page
      title={t("startSession.title", { name })}
      helpButton
      bottomButton={
        <ButtonDS
          format="fill"
          buttonType="primary"
          text={t("restitution.button.continue")}
          disabled={hasError}
          onClick={handleSubmit}
        />
      }
    >
      <StyledForm>
        <TextCapitalized>{t("startSession.text")}</TextCapitalized>
        <Spacer y={2} />
        <DateInput
          label={t("startSession.date.label")}
          value={selectedDate}
          onChange={setSelectedDate}
          min={minDateTime ?? undefined}
          max={maxDateTime}
          error={displayError ? dateError : undefined}
        />
        <TextCapitalized>{t("startSession.hour.label")}</TextCapitalized>
        <Spacer y={0.5} />
        <HourMinuteInput
          value={selectedDate}
          onChange={setSelectedDate}
          error={displayError && hasError}
        />
      </StyledForm>
    </Page>
  );
};

const StyledForm = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
